// StudioPanel — Individual panel wrapper with source selector toolbar
// Each panel has its own toolbar to choose content type + zoom controls
import { useState, useRef, useCallback, lazy, Suspense } from "react";
import { useRecordingStore } from "@/stores/recording-store";
import { useMagnifierStore } from "@/stores/magnifier-store";
import {
  Terminal,
  FileText,
  Pencil,
  MonitorPlay,
  Globe,
  Camera,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  GripVertical,
  ChevronDown,
  Loader2,
} from "lucide-react";

// Dynamic imports for panel content
const PanelTerminal = lazy(() => import("./panels/PanelTerminal"));
const PanelWhiteboard = lazy(() => import("./panels/PanelWhiteboard"));
const PanelPDFViewer = lazy(() => import("./panels/PanelPDFViewer"));
const PanelScreenShare = lazy(() => import("./panels/PanelScreenShare"));
const PanelBrowser = lazy(() => import("./panels/PanelBrowser"));
const PanelWebcam = lazy(() => import("./panels/PanelWebcam"));

const PANEL_SOURCES = [
  { type: "terminal", label: "Terminal", icon: Terminal, color: "#22c55e" },
  { type: "pdf", label: "PDF Viewer", icon: FileText, color: "#3b82f6" },
  { type: "whiteboard", label: "Tableau Blanc", icon: Pencil, color: "#f97316" },
  { type: "screen-share", label: "Partage d'écran", icon: MonitorPlay, color: "#8b5cf6" },
  { type: "browser", label: "Navigateur", icon: Globe, color: "#ef4444" },
  { type: "webcam", label: "Webcam", icon: Camera, color: "#06b6d4" },
];

const PANEL_COMPONENTS = {
  terminal: PanelTerminal,
  pdf: PanelPDFViewer,
  whiteboard: PanelWhiteboard,
  "screen-share": PanelScreenShare,
  browser: PanelBrowser,
  webcam: PanelWebcam,
};

const PanelFallback = () => (
  <div className="h-full w-full flex items-center justify-center" style={{ background: "#1a1a2e" }}>
    <Loader2 className="h-6 w-6 text-white/30 animate-spin" />
  </div>
);

// These panels render their content inside a nested browsing context (<iframe> for the
// browser and Google Slides, <embed> for the PDF plugin). Mouse events over a nested
// context are delivered to that document, never to this page — so the magnifier, which
// tracks the cursor on `window`, freezes as soon as the pointer enters them. While the
// magnifier is on we cover them with a transparent layer that captures the movement.
const NESTED_CONTEXT_PANELS = new Set(["pdf", "browser"]);

export default function StudioPanel({ panel, index, isRecording = false }) {
  const { setPanelType, setPanelZoom } = useRecordingStore();
  const magnifierEnabled = useMagnifierStore((s) => s.isEnabled);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const panelRef = useRef(null);

  const currentSource = PANEL_SOURCES.find((s) => s.type === panel.type) || PANEL_SOURCES[0];
  const CurrentIcon = currentSource.icon;
  const PanelComponent = PANEL_COMPONENTS[panel.type];

  const handleZoomIn = useCallback(() => {
    setPanelZoom(panel.id, (panel.zoom || 1) + 0.2);
  }, [panel.id, panel.zoom, setPanelZoom]);

  const handleZoomOut = useCallback(() => {
    setPanelZoom(panel.id, (panel.zoom || 1) - 0.2);
  }, [panel.id, panel.zoom, setPanelZoom]);

  const handleZoomReset = useCallback(() => {
    setPanelZoom(panel.id, 1);
  }, [panel.id, setPanelZoom]);

  const toggleFullscreen = useCallback(() => {
    if (!panelRef.current) return;
    if (!isFullscreen) {
      panelRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  return (
    <div
      ref={panelRef}
      className="flex flex-col rounded-xl overflow-hidden border border-white/10 bg-[#12121f] h-full relative group"
      data-panel-id={panel.id}
    >
      {/* ── Panel Toolbar ── */}
      <div className="flex items-center justify-between px-2 py-1.5 bg-black/50 border-b border-white/5 shrink-0 z-10">
        {/* Left: Source selector */}
        <div className="relative">
          <button
            onClick={() => !isRecording && setShowSourceMenu(!showSourceMenu)}
            disabled={isRecording}
            className="flex items-center gap-2 px-2.5 py-1 rounded-md hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            <CurrentIcon className="h-3.5 w-3.5" style={{ color: currentSource.color }} />
            <span className="text-[11px] font-medium text-white/80">{currentSource.label}</span>
            {!isRecording && <ChevronDown className="h-3 w-3 text-white/40" />}
          </button>

          {/* Source dropdown */}
          {showSourceMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowSourceMenu(false)} />
              <div className="absolute top-full left-0 mt-1 z-50 w-48 rounded-xl bg-[#1e1e32] border border-white/10 shadow-2xl shadow-black/50 py-1 animate-in fade-in slide-in-from-top-2 duration-150">
                {PANEL_SOURCES.map((source) => {
                  const Icon = source.icon;
                  const isActive = panel.type === source.type;
                  return (
                    <button
                      key={source.type}
                      onClick={() => {
                        setPanelType(panel.id, source.type);
                        setShowSourceMenu(false);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                        isActive ? "bg-white/10" : "hover:bg-white/5"
                      }`}
                    >
                      <Icon className="h-4 w-4" style={{ color: source.color }} />
                      <span className={`text-xs ${isActive ? "text-white font-medium" : "text-white/70"}`}>
                        {source.label}
                      </span>
                      {isActive && (
                        <div className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: source.color }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Center: Panel number */}
        <span className="text-[10px] text-white/20 font-mono">#{index + 1}</span>

        {/* Right: Zoom controls */}
        <div className="flex items-center gap-0.5">
          <button onClick={handleZoomOut} className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70" title="Zoom arrière">
            <ZoomOut className="h-3 w-3" />
          </button>
          <span className="text-[9px] text-white/30 font-mono w-8 text-center">
            {Math.round((panel.zoom || 1) * 100)}%
          </span>
          <button onClick={handleZoomIn} className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70" title="Zoom avant">
            <ZoomIn className="h-3 w-3" />
          </button>
          <button onClick={handleZoomReset} className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70" title="Réinitialiser zoom">
            <RotateCcw className="h-3 w-3" />
          </button>
          <button onClick={toggleFullscreen} className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70" title="Plein écran">
            <Maximize2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* ── Panel Content ── */}
      <div className="flex-1 overflow-hidden relative">
        <Suspense fallback={<PanelFallback />}>
          {PanelComponent ? (
            <PanelComponent
              zoom={panel.zoom || 1}
              settings={panel.settings || {}}
            />
          ) : (
            <PanelFallback />
          )}
        </Suspense>

        {/* Lets the magnifier keep tracking the cursor over iframe/embed content.
            Trade-off: while the magnifier is on, these panels are hover-only. */}
        {magnifierEnabled && NESTED_CONTEXT_PANELS.has(panel.type) && (
          <div
            className="absolute inset-0 z-20"
            style={{ cursor: "crosshair", background: "transparent" }}
            title="Loupe active — désactivez-la (Alt+Z) pour interagir avec ce panneau"
            aria-hidden="true"
          />
        )}
      </div>

      {/* Drag handle (visible on hover) */}
      <div className="absolute top-1/2 -translate-y-1/2 left-0 opacity-0 group-hover:opacity-60 transition-opacity cursor-grab p-0.5">
        <GripVertical className="h-4 w-4 text-white/30" />
      </div>
    </div>
  );
}
